import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UserService } from 'app/library/user/services/user.service';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { IEntityState } from 'app/projects/entity/src/lib/interfaces/entity.state.interface';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { RefreshUsersOnlineState } from 'app/projects/user/src/lib/actions/refresh-users-online-state.action';
import {
    AddUsersToTeamsAsLeaders,
    AddUsersToTeamsAsLeadersFailure,
    AddUsersToTeamsAsLeadersSuccess,
    AddUsersToTeamsAsMembers,
    AddUsersToTeamsAsMembersFailure,
    AddUsersToTeamsAsMembersSuccess,
    AddUsersToTeamsAsWatchers,
    AddUsersToTeamsAsWatchersFailure,
    AddUsersToTeamsAsWatchersSuccess,
    CreateTeam,
    CreateTeamFailure,
    CreateTeamSuccess,
    DeleteTeam,
    DeleteTeamFailure,
    DeleteTeamSuccess,
    FetchRootTeams,
    FetchTeam,
    FetchTeamMembers,
    FetchTeamChildren,
    FetchTeamChildrenFailure,
    FetchTeamChildrenSuccess,
    FetchTeamFailure,
    FetchTeamParent,
    FetchTeamParentFailure,
    FetchTeamParentSuccess,
    FetchTeams,
    FetchTeamsFailure,
    FetchTeamsSuccess,
    FetchTeamSuccess,
    FetchTeamWithSubTeamsProgress,
    FetchTeamWithSubTeamsProgressFailure,
    FetchTeamWithSubTeamsProgressSuccess,
    SetTeam,
    SetTeams,
    StopWatchingTeam,
    StopWatchingTeamFailure,
    StopWatchingTeamSuccess,
    UnsetTeam,
    UpdateTeam,
    UpdateTeamFailure,
    UpdateTeamSuccess,
} from '../actions';
import { AddTeams } from '../actions/add-entities.action';
import { TeamApiService } from '../services/team.api-service';
import { FUSE_ASYNC_DRAWER } from 'app/projects/fuse/src/lib/services/async-drawer.token';
import { AsyncJobApiService } from 'app/services/async-job.api-service';
import { Team } from './team.model';
import { TeamStateModel } from './team.state-model';

@State<TeamStateModel>({
    name: 'team',
    defaults: new TeamStateModel(),
})
@Injectable()
export class TeamState extends EntityState<Team> implements IEntityState<Team> {
    static sortProperties: string[] = ['name'];

    @Selector()
    static getEntity(state: TeamStateModel, id: string = state.item): Team {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntities(state: TeamStateModel): Team[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(TeamState.sortProperties));

        return toReturn;
    }

    @Selector()
    static getRootTeams(state: TeamStateModel): Team[] {
        return state.rootTeams;
    }

    constructor(
        private _teamApiService: TeamApiService,
        private _store: Store,
        private _userService: UserService,
        private _featureService: FeatureService,
        @Inject(FUSE_ASYNC_DRAWER) private _asyncJobApiService: AsyncJobApiService
    ) {
        super(_teamApiService, 0);
    }

    @Action(AddTeams)
    addEntities(ctx: StateContext<TeamStateModel>, action: AddTeams): void {
        this._addEntities(ctx, action);
    }

    @Action(SetTeam)
    setEntity(ctx: StateContext<TeamStateModel>, action: SetTeam): void {
        this._setEntity(ctx, action, AddTeams);

        setTimeout(() => {
            const lowThree = action.payload.lowThree;
            if (lowThree && this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState)) {
                this._store.dispatch(new RefreshUsersOnlineState(lowThree));
                this._userService.setOnlineStateInterval(RefreshUsersOnlineState, lowThree);
            }

            const topThree = action.payload.topThree;
            if (topThree && this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState)) {
                this._store.dispatch(new RefreshUsersOnlineState(topThree));
                this._userService.setOnlineStateInterval(RefreshUsersOnlineState, topThree);
            }
        });
    }

    @Action(UnsetTeam)
    unsetEntity(ctx: StateContext<TeamStateModel>): void {
        this._unsetEntity(ctx);
    }

    @Action(CreateTeam)
    createEntity(ctx: StateContext<TeamStateModel>, action: CreateTeam): void {
        this._createEntity(ctx, action, CreateTeamSuccess, CreateTeamFailure, AddTeams).then(() => {});
    }

    @Action(UpdateTeam)
    updateEntity(ctx: StateContext<TeamStateModel>, action: UpdateTeam): void {
        this._updateEntity(ctx, action, UpdateTeamSuccess, UpdateTeamFailure, AddTeams).then(() => {});
    }

    @Action(DeleteTeam)
    deleteEntity(ctx: StateContext<TeamStateModel>, action: DeleteTeam): void {
        this._deleteEntity(ctx, action, DeleteTeamSuccess, DeleteTeamFailure).then(() => {});
    }

    @Action(FetchTeam)
    fetchEntity(ctx: StateContext<TeamStateModel>, action: FetchTeam): void {
        this._fetchEntity(ctx, action, FetchTeamSuccess, FetchTeamFailure, AddTeams, SetTeam).then(() => {});
    }

    @Action(FetchTeamSuccess)
    fetchEntitySuccess(ctx: StateContext<TeamStateModel>, action: FetchTeamSuccess): void {
        this._fetchEntitySuccess(ctx, action);
    }

    @Action(FetchTeamFailure)
    fetchEntityFailure(ctx: StateContext<TeamStateModel>, action: FetchTeamFailure): void {
        this._fetchEntityFailure(ctx, action);
    }

    @Action(SetTeams)
    setEntities(ctx: StateContext<TeamStateModel>, action: SetTeams): void {
        /* Fetching Teams with less data would override current team with full data */
        const currentTeam = this._store.selectSnapshot(TeamState.getEntity);
        if (currentTeam) {
            const updatedPayload = action.payload.map((team) => {
                return team.id === currentTeam.id ? currentTeam : team;
            });

            action.payload = updatedPayload;
        }

        this._setEntities(ctx, action);
    }

    @Action(FetchTeams)
    fetchEntities(ctx: StateContext<TeamStateModel>): void {
        this._fetchEntities(ctx, FetchTeamsSuccess, FetchTeamsFailure, SetTeams).then(() => {});
    }

    @Action(FetchTeamsSuccess)
    fetchEntitiesSuccess(ctx: StateContext<TeamStateModel>): void {
        this._fetchEntitiesSuccess(ctx);
    }

    @Action(FetchTeamsFailure)
    fetchEntitiesFailure(ctx: StateContext<TeamStateModel>): void {
        this._fetchEntitiesFailure(ctx);
    }

    @Action(FetchRootTeams)
    fetchRootTeams(ctx: StateContext<TeamStateModel>, action: FetchRootTeams): void {
        this._teamApiService.getRootTeams().subscribe(
            (entities) => {
                ctx.dispatch(new FetchTeamsSuccess());

                ctx.patchState({
                    rootTeams: entities,
                });
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamsFailure());
            }
        );
    }

    @Action(FetchTeamChildren)
    fetchTeamChildren(ctx: StateContext<TeamStateModel>, action: FetchTeamChildren): void {
        this._teamApiService.getTeamChildren(action.payload).subscribe(
            (entities) => {
                ctx.dispatch(new FetchTeamChildrenSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamChildrenFailure());
            }
        );
    }

    @Action(FetchTeamParent)
    fetchTeamParent(ctx: StateContext<TeamStateModel>, action: FetchTeamParent): void {
        this._teamApiService.getTeamParent(action.payload).subscribe(
            (entity) => {
                ctx.dispatch(new FetchTeamParentSuccess(entity));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamParentFailure());
            }
        );
    }

    @Action(FetchTeamWithSubTeamsProgress)
    fetchTeamWithSubTeamsProgress(ctx: StateContext<TeamStateModel>, action: FetchTeamWithSubTeamsProgress): void {
        this._teamApiService.getTeamWithSubTeamsProgress(action.payload.teamId, action.payload.includeSubTeamsProgress).subscribe(
            (entity) => {
                ctx.dispatch([new FetchTeamWithSubTeamsProgressSuccess(entity), new SetTeam(entity)]);
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamWithSubTeamsProgressFailure({ entityId: action.payload.teamId, response }));
            }
        );
    }

    /* Team Members */
    @Action(AddUsersToTeamsAsMembers)
    addUsersToModulesAsMembers({ dispatch }: StateContext<TeamStateModel>, { payload }: AddUsersToTeamsAsMembers): void {
        this._teamApiService.addUsersToTeamAsMembers(payload).subscribe(
            (result) => {
                dispatch(new AddUsersToTeamsAsMembersSuccess(result));
                if (result.asyncJobId) {
                    this._asyncJobApiService.trackEnqueuedJob(result.asyncJobId, () => {
                        dispatch(payload.teamIds.map((teamId) => new FetchTeamMembers({ teamId })));
                    });
                }
            },
            (response: HttpErrorResponse) => {
                dispatch(new AddUsersToTeamsAsMembersFailure({ response }));
            }
        );
    }

    /* Team Leaders */
    @Action(AddUsersToTeamsAsLeaders)
    addUsersToModulesAsLeaders({ dispatch }: StateContext<TeamStateModel>, { payload }: AddUsersToTeamsAsLeaders): void {
        this._teamApiService.addUsersToTeamAsLeaders(payload).subscribe(
            (entities) => {
                dispatch(new AddUsersToTeamsAsLeadersSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new AddUsersToTeamsAsLeadersFailure({ response }));
            }
        );
    }

    /* Team Watchers */
    @Action(AddUsersToTeamsAsWatchers)
    addUsersToModulesAsWatchers({ dispatch }: StateContext<TeamStateModel>, { payload }: AddUsersToTeamsAsWatchers): void {
        this._teamApiService.addUsersToTeamAsWatchers(payload).subscribe(
            (entities) => {
                dispatch(new AddUsersToTeamsAsWatchersSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new AddUsersToTeamsAsWatchersFailure({ response }));
            }
        );
    }

    @Action(StopWatchingTeam)
    stopWatchingTeam({ dispatch }: StateContext<TeamStateModel>, action: StopWatchingTeam): void {
        this._teamApiService.stopWatchingTeam(action.teamId).subscribe(
            () => {
                dispatch(new StopWatchingTeamSuccess());
            },
            (response: HttpErrorResponse) => {
                dispatch(new StopWatchingTeamFailure());
            }
        );
    }
}
